import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { cn } from '@/lib/cn'
import EphemeralNotice from '@/components/layout/EphemeralNotice'
import ErrorBoundary from '@/components/layout/ErrorBoundary'
import EventStream from '@/components/layout/EventStream'
import KeyboardShortcuts from '@/components/layout/KeyboardShortcuts'
import SessionHeartbeat from '@/components/layout/SessionHeartbeat'
import Sidebar from '@/components/layout/Sidebar'
import TopBar from '@/components/layout/TopBar'
import UnsavedWorkGuard from '@/components/layout/UnsavedWorkGuard'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

interface AppShellProps {
  children: ReactNode
}

export default function AppShell({ children }: AppShellProps): React.ReactElement {
  const { pathname } = useLocation()
  const [navOpen, setNavOpen] = useState(false)
  const drawerRef = useRef<HTMLDivElement>(null)

  const closeNav = useCallback(() => setNavOpen(false), [])

  useEffect(() => {
    setNavOpen(false)
  }, [pathname])

  // The drawer covers the page, so it behaves like a dialog: focus moves in,
  // Tab cycles inside it, and focus returns to whatever opened it.
  useEffect(() => {
    if (!navOpen) return
    const panel = drawerRef.current
    if (!panel) return

    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const focusable = (): HTMLElement[] =>
      Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (element) => element.tabIndex >= 0 && element.offsetParent !== null
      )

    focusable()[0]?.focus()

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setNavOpen(false)
        return
      }
      if (event.key !== 'Tab') return

      const items = focusable()
      const first = items[0]
      const last = items[items.length - 1]
      if (!first || !last) return

      const active = document.activeElement
      if (event.shiftKey && (active === first || !panel.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      opener?.focus()
    }
  }, [navOpen])

  return (
    <KeyboardShortcuts>
      <div className="relative flex h-full overflow-hidden bg-canvas">
        {/* Parked off-screen rather than `sr-only`, so nothing depends on the
            generated order of `not-sr-only` and the position utilities. */}
        <a
          href="#main-content"
          className="absolute top-3 left-3 z-50 -translate-y-24 rounded-control bg-accent px-3 py-2 text-sm font-medium text-accent-fg opacity-0 transition-[transform,opacity] duration-150 ease-out-quart focus:translate-y-0 focus:opacity-100"
        >
          Skip to content
        </a>

        <aside className="hidden w-60 shrink-0 border-r border-line lg:block">
          <Sidebar />
        </aside>

        {/* Kept mounted so it can animate both ways; `visibility` also takes it
            out of the tab order while closed, which `opacity` alone would not. */}
        <div
          id="app-nav-drawer"
          ref={drawerRef}
          className={cn('fixed inset-0 z-40 lg:hidden', !navOpen && 'pointer-events-none')}
          inert={!navOpen}
        >
          <button
            type="button"
            tabIndex={-1}
            aria-label="Close navigation"
            onClick={closeNav}
            className={cn(
              'absolute inset-0 h-full w-full cursor-default bg-ink-950/45 backdrop-blur-[2px] transition-[opacity,visibility] duration-200 ease-out-quart dark:bg-ink-950/70',
              navOpen ? 'visible opacity-100' : 'invisible opacity-0'
            )}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            className={cn(
              'absolute inset-y-0 left-0 w-[17rem] max-w-[85vw] border-r border-line shadow-pop transition-[transform,visibility] duration-200 ease-out-quart',
              navOpen ? 'visible translate-x-0' : 'invisible -translate-x-full'
            )}
          >
            <Sidebar onNavigate={closeNav} onClose={closeNav} />
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar navOpen={navOpen} onOpenNav={() => setNavOpen(true)} />

          {/* Above the scroll container, not inside it: nothing here is persisted, so
              the warning that the session is about to take the work with it must not
              be something a user can scroll away from. */}
          <EphemeralNotice />

          <main
            id="main-content"
            tabIndex={-1}
            className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto"
          >
            <div className="mx-auto w-full max-w-[90rem] px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
              <ErrorBoundary>{children}</ErrorBoundary>
            </div>
          </main>
        </div>

        <EventStream />
        {/* Render nothing. The heartbeat slides the idle window while this tab is
            visible — without it an open tab is swept after SESSION_IDLE_TTL_MS and
            takes the connected site with it — and the guard intercepts a reload or
            close while generated alt text is unsaved. */}
        <SessionHeartbeat />
        <UnsavedWorkGuard />
      </div>
    </KeyboardShortcuts>
  )
}

export { AppShell }
